import { FC, useState } from "react";
import { Autocomplete, Box, Grid, IconButton, Popover, TextField, Tooltip, Typography } from "@material-ui/core";
import { Cancel, Info } from "@material-ui/icons";
import ColorPickerField from "./ColorPickerField";
import { FieldProps } from "./Types";
import { transformColorFormat } from "./Helpers";
import PencilAlt from "src/icons/PencilAlt";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  colorBox: {
    width: '24px',
    height: '24px',
    border: '1px solid black',
    borderRadius: '4px',
    marginLeft: '5px',
    marginRight: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: '24px',
  },
  customInput: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid #ccc',
    borderRadius: '10px',
    padding: '10px',
    minHeight: '56px',
    height: '100%',
  },
  customTextField: {
    flexGrow: 1,
  },
  crossIcon: {
    color: 'red',
    fontSize: '16px',
  },
  popoverContent: {
    padding: '10px',
    boxShadow: '0px 4px 10px lightgrey',
    width: '340px', // Ensures the width is only as wide as the content
  },
  colorPickerContainer: {
    width: '300px', // Set a specific width for the color picker field
  },
}));

const Field: FC<FieldProps> = (props) => {
  const { label, colorKey, fontSizeKey, fontFamilyKey, onChange, themeType, fontSizeOptions, fontFamilyOptions, initialColor = null, initialFontSize = null, initialFontFamily = null, description = null } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const maxLength = 35;
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const boxStyle = { backgroundColor: initialColor ? transformColorFormat(initialColor) : 'transparent' };

  const breakLabel = (label: string) => {
    const excludedWords = ["for", "in"]; // Words to be excluded

    if (label.length > maxLength) {
      const lastSpaceIndex = label.lastIndexOf(" ", maxLength);
      if (lastSpaceIndex !== -1) {
        const truncatedLabel = label.substring(0, lastSpaceIndex);
        const lastWord = truncatedLabel.split(" ").pop();
        if (excludedWords.includes(lastWord)) {
          const prevSpaceIndex = truncatedLabel.lastIndexOf(" ", lastSpaceIndex - 1);
          if (prevSpaceIndex !== -1) {
            return truncatedLabel.substring(0, prevSpaceIndex) + "...";
          }
        }
        return truncatedLabel + "...";
      }
    }

    return label;
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getInitialOption = (options: any, value: any) => {
    return options.find((option: any) => option.Value === value) || null;
  };

  const handleChange = async (type: string, newValue: any) => {
    let fieldKey = null;
    let value = newValue;
    if (type === 'color') {
      fieldKey = themeType ? `${themeType}.${colorKey}` : colorKey;
    } else if (type === 'fontSize') {
      fieldKey = themeType ? `${themeType}.${fontSizeKey}` : fontSizeKey;
    } else {
      fieldKey = themeType ? `${themeType}.${fontFamilyKey}` : fontFamilyKey;
      value = newValue.Value;
    }
    return await onChange(fieldKey, value);
  };

  return (
    <>
      <div style={{ height: '100%' }}>
        <Box className={classes.customInput}>
          <Box display='flex' flexDirection='column'>
            <Typography variant="subtitle2" className={classes.customTextField}>
              {breakLabel(label)}
            </Typography>
            <Typography variant="caption" className={classes.customTextField}>
              <span style={{ fontWeight: 'bold' }}>Color: </span>
              {initialColor ? transformColorFormat(initialColor) : 'N/A'}
            </Typography>
            <Typography variant="caption" className={classes.customTextField}>
              <span style={{ fontWeight: 'bold' }}>Font: </span>
              {initialFontFamily
                ? `${getInitialOption(fontFamilyOptions, initialFontFamily)?.Label || ''}`
                : 'N/A'}
            </Typography>
            <Typography variant="caption" className={classes.customTextField}>
              <span style={{ fontWeight: 'bold' }}>Size: </span>
              {initialFontSize ? `${initialFontSize}` : 'N/A'}
            </Typography>
          </Box>

          <Box display='flex' flexDirection='row' alignItems='center'>
            <Box display='flex' alignItems='center'>
              {((description && description !== "") || label.length > maxLength) && (
                <Tooltip
                  title={
                    <div>
                      {label.length > maxLength && <span><b>{label}</b></span>}
                      {label.length > maxLength && <br />}
                      {description && <span><i>{description}</i></span>}
                    </div>
                  }
                >
                  <IconButton size="small">
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box className={classes.colorBox} style={boxStyle}>
              {!initialColor && <Cancel className={classes.crossIcon} />}
            </Box>

            <Box display='flex' alignItems='center'>
              <IconButton size="small" onClick={handleClick}>
                <PencilAlt fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Popover
          id={id}
          open={open}
          anchorReference="anchorPosition"
          anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
        >
          <Box className={classes.popoverContent}>
            <Grid container spacing={1} justifyContent='center'>
              <Grid item xs={12} style={{ display: 'flex', flexDirection: 'row' }} justifyContent='space-between'>
                <Typography>{label}</Typography>
                {description && (
                  <Tooltip title={<span><i>{description}</i></span>}>
                    <IconButton size="small">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>

              {initialColor && (
                <Grid item xs={12}>
                  <div className={classes.colorPickerContainer}>
                    <ColorPickerField initialColor={initialColor} handleChange={handleChange} />
                  </div>
                </Grid>
              )}

              {initialFontSize && (
                <Grid item xs={4}>
                  <Autocomplete
                    options={fontSizeOptions}
                    renderInput={(params) => <TextField {...params} label="Font Size" variant="outlined" />}
                    style={{ marginBottom: '16px' }}
                    onChange={(event, newValue) => handleChange('fontSize', newValue)}
                    value={initialFontSize}
                    disableClearable
                    size="small"
                  />
                </Grid>
              )}

              {initialFontFamily && (
                <Grid item xs={8}>
                  <Autocomplete
                    options={fontFamilyOptions}
                    renderInput={(params) => <TextField {...params} label="Font Family" variant="outlined" />}
                    style={{ marginBottom: '16px' }}
                    onChange={(event, newValue) => handleChange('fontFamily', newValue)}
                    value={getInitialOption(fontFamilyOptions, initialFontFamily)}
                    getOptionLabel={(option) => option.Label}
                    getOptionSelected={(option, value) => option.Value === initialFontFamily}
                    disableClearable
                    size="small"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Popover>
      </div>
    </>
  );
};

export default Field;
